import { useMemo, useState } from "react";
import { Check, Settings2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SectionCard, InfoNote, Segmented } from "@/components/common/Cards";
import { Field, NumInput, MoneyInput } from "@/components/common/Inputs";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtRp } from "@/lib/format";

export default function InventoryPanel({ servers, data, setData, setParams, readOnly, fleet, onReload }) {
  const selected = data.selectedServerIds; // null = all
  const allIds = useMemo(() => (servers || []).map((s) => s.id), [servers]);
  const isAllSelected = selected === null || selected.length === allIds.length;
  const [bulk, setBulk] = useState({ purchase_price: "", depreciation_months: "", power_watt: "", ip_count: "", license_monthly: "" });
  const [applying, setApplying] = useState(false);

  const toggleAll = (v) => {
    setData((d) => ({ ...d, selectedServerIds: v ? null : [] }));
  };
  const toggleOne = (id) => {
    setData((d) => {
      const cur = d.selectedServerIds === null ? allIds.slice() : d.selectedServerIds.slice();
      const i = cur.indexOf(id);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(id);
      return { ...d, selectedServerIds: cur };
    });
  };

  const applyBulk = async () => {
    const ids = selected === null ? allIds : selected;
    const fields = {};
    Object.entries(bulk).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) fields[k] = Number(v);
    });
    if (!Object.keys(fields).length) return toast.error("Isi minimal satu kolom biaya");
    if (!ids.length) return toast.error("Pilih minimal 1 server");
    setApplying(true);
    try {
      await api.post("/servers/bulk-update", { ids, fields });
      toast.success(`${ids.length} server diperbarui`);
      setBulk({ purchase_price: "", depreciation_months: "", power_watt: "", ip_count: "", license_monthly: "" });
      onReload && onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal update"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard title="Parameter kapasitas & biaya umum" eyebrow="Model" testid="hosting-params-card">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Overcommit vCPU" hint="1 = tanpa overcommit; 2 = 2x oversubscribe.">
            <NumInput value={data.params.cpuOvercommit} onChange={(v) => setParams((p) => ({ ...p, cpuOvercommit: v }))} suffix="x" decimals={2} disabled={readOnly} testid="hosting-param-cpu-overcommit" />
          </Field>
          <Field label="Overcommit RAM">
            <NumInput value={data.params.ramOvercommit} onChange={(v) => setParams((p) => ({ ...p, ramOvercommit: v }))} suffix="x" decimals={2} disabled={readOnly} testid="hosting-param-ram-overcommit" />
          </Field>
          <Field label="Overcommit Disk">
            <NumInput value={data.params.diskOvercommit} onChange={(v) => setParams((p) => ({ ...p, diskOvercommit: v }))} suffix="x" decimals={2} disabled={readOnly} testid="hosting-param-disk-overcommit" />
          </Field>
          <Field label="Cadangan sistem (CPU)">
            <NumInput value={data.params.cpuReservePct} onChange={(v) => setParams((p) => ({ ...p, cpuReservePct: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-cpu-reserve" />
          </Field>
          <Field label="Cadangan sistem (RAM)">
            <NumInput value={data.params.ramReservePct} onChange={(v) => setParams((p) => ({ ...p, ramReservePct: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-ram-reserve" />
          </Field>
          <Field label="Target utilisasi" hint="Target maksimum pemakaian server (sisanya untuk burst).">
            <NumInput value={data.params.targetUtilPct} onChange={(v) => setParams((p) => ({ ...p, targetUtilPct: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-target-util" />
          </Field>
          <Field label="Maintenance per tahun" hint="% harga hardware/tahun untuk maintenance & spare.">
            <NumInput value={data.params.maintenancePctYear} onChange={(v) => setParams((p) => ({ ...p, maintenancePctYear: v }))} suffix="%/yr" decimals={1} disabled={readOnly} testid="hosting-param-maintenance" />
          </Field>
          <Field label="Staf per server/bulan" hint="Alokasi biaya SDM operasional per server.">
            <MoneyInput value={data.params.staffPerServer} onChange={(v) => setParams((p) => ({ ...p, staffPerServer: v }))} disabled={readOnly} testid="hosting-param-staff" />
          </Field>
          <Field label="Bobot alokasi biaya (CPU)" hint="Pembagian biaya server berdasarkan CPU vs RAM vs Disk.">
            <NumInput value={data.params.wCpu} onChange={(v) => setParams((p) => ({ ...p, wCpu: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-wcpu" />
          </Field>
          <Field label="Bobot RAM">
            <NumInput value={data.params.wRam} onChange={(v) => setParams((p) => ({ ...p, wRam: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-wram" />
          </Field>
          <Field label="Bobot Disk">
            <NumInput value={data.params.wDisk} onChange={(v) => setParams((p) => ({ ...p, wDisk: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-wdisk" />
          </Field>
          <Field label="Target margin">
            <NumInput value={data.params.marginPct} onChange={(v) => setParams((p) => ({ ...p, marginPct: v }))} suffix="%" decimals={0} disabled={readOnly} testid="hosting-param-margin" />
          </Field>
        </div>
      </SectionCard>

      <SectionCard
        title={`Inventory Server (${servers?.length || 0})`}
        description="Centang server yang masuk perhitungan. Nilai 'Alokasi VM' dihitung real-time dari VM Running."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="hosting-select-all">
              <Checkbox checked={isAllSelected} onCheckedChange={toggleAll} disabled={readOnly} /> Semua
            </label>
          </div>
        }
        bodyClassName="p-0"
        testid="hosting-inventory-card"
      >
        <div className="overflow-x-auto scroll-gold">
          <table className="dense-table w-full text-xs" data-testid="hosting-inventory-table">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-4 py-3 w-10"></th>
                <th className="text-left px-3 py-3">Hostname</th>
                <th className="text-left px-3 py-3">Lokasi</th>
                <th className="text-left px-3 py-3">Role</th>
                <th className="text-right px-3 py-3">Core/Thread</th>
                <th className="text-right px-3 py-3">RAM</th>
                <th className="text-right px-3 py-3">Disk</th>
                <th className="text-right px-3 py-3">Harga</th>
                <th className="text-right px-3 py-3">Daya (W)</th>
                <th className="text-right px-3 py-3">Alokasi VM</th>
                <th className="text-right px-3 py-3">Biaya/bulan</th>
              </tr>
            </thead>
            <tbody>
              {(servers || []).map((s) => {
                const inSel = selected === null || selected.includes(s.id);
                const r = (fleet.rows || []).find((x) => x.server.id === s.id);
                return (
                  <tr key={s.id} className={`border-t border-border ${inSel ? "" : "opacity-50"} hover:bg-[hsl(220_10%_9%)]`} data-testid={`hosting-row-${s.hostname}`}>
                    <td className="px-4 py-2.5">
                      <Checkbox checked={inSel} onCheckedChange={() => toggleOne(s.id)} disabled={readOnly} data-testid={`hosting-row-check-${s.hostname}`} />
                    </td>
                    <td className="px-3 py-2.5 font-medium">{s.hostname}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{s.location}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className="border-border text-[10px] text-muted-foreground">{s.role}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right num">{s.cores}/{s.threads}</td>
                    <td className="px-3 py-2.5 text-right num">{fmtNum(s.ram_gb)} GB</td>
                    <td className="px-3 py-2.5 text-right num">{fmtNum(s.storage_tb)} TB</td>
                    <td className="px-3 py-2.5 text-right num text-muted-foreground">{s.purchase_price ? fmtRp(s.purchase_price) : "–"}</td>
                    <td className="px-3 py-2.5 text-right num text-muted-foreground">{s.power_watt || "–"}</td>
                    <td className="px-3 py-2.5 text-right num text-primary">{s.allocated?.count || 0}</td>
                    <td className="px-3 py-2.5 text-right num">{r ? fmtRp(r.cost.total) : "–"}</td>
                  </tr>
                );
              })}
              {(!servers || servers.length === 0) && (
                <tr>
                  <td colSpan={11} className="p-10 text-center text-muted-foreground">Belum ada server. Admin bisa impor via tab Inventory di Admin.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="Isi biaya hardware sekaligus"
        eyebrow="Bulk update"
        description="Terapkan nilai biaya ke server yang dicentang. Kolom kosong tidak diubah."
        testid="hosting-bulk-card"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Harga beli (Rp)">
            <MoneyInput value={bulk.purchase_price} onChange={(v) => setBulk({ ...bulk, purchase_price: v })} disabled={readOnly} testid="hosting-bulk-price" />
          </Field>
          <Field label="Depresiasi (bulan)">
            <NumInput value={bulk.depreciation_months} onChange={(v) => setBulk({ ...bulk, depreciation_months: v })} decimals={0} suffix="bln" disabled={readOnly} testid="hosting-bulk-depre" />
          </Field>
          <Field label="Daya rata-rata">
            <NumInput value={bulk.power_watt} onChange={(v) => setBulk({ ...bulk, power_watt: v })} decimals={0} suffix="W" disabled={readOnly} testid="hosting-bulk-power" />
          </Field>
          <Field label="IPv4 per server">
            <NumInput value={bulk.ip_count} onChange={(v) => setBulk({ ...bulk, ip_count: v })} decimals={0} disabled={readOnly} testid="hosting-bulk-ip" />
          </Field>
          <Field label="Lisensi/bulan">
            <MoneyInput value={bulk.license_monthly} onChange={(v) => setBulk({ ...bulk, license_monthly: v })} disabled={readOnly} testid="hosting-bulk-license" />
          </Field>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={applyBulk} disabled={applying || readOnly} data-testid="hosting-bulk-apply">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />} Terapkan ke server dicentang
          </Button>
        </div>
        {fleet.missingPrice > 0 && (
          <InfoNote className="mt-3">
            <b>{fleet.missingPrice}</b> server belum memiliki harga beli. COGS belum lengkap. Isi harga di sini atau per-server lewat admin.
          </InfoNote>
        )}
      </SectionCard>
    </div>
  );
}
